import * as React from 'react';
import {
  AutocompleteRenderOptionState,
  AutocompleteProps as MuiAutocompleteProps,
  AutocompleteRenderInputParams as RenderInputParams,
} from '@material-ui/lab/Autocomplete';
import { IconButton, TextField, TextFieldProps } from 'omakase';
import { IconClose } from 'ui/icons';
import { IconCrossLarge } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Autocomplete as MuiAutocomplete } from '@material-ui/lab';
import { Popper, PopperProps } from '@material-ui/core';
import { popper, textField } from 'omakase/FormControls/autocompleteCssDefs';
import ListItem from 'omakase/ListItem/ListItem';

export interface AutocompleteProps<T>
  extends Omit<MuiAutocompleteProps<T, undefined, undefined, undefined>, 'renderInput'> {
  autofocus?: boolean;
  label?: string;
  renderInput?(params: RenderInputParams): JSX.Element;
  TextFieldProps?: Optional<TextFieldProps>;
  onChange(changeEvent: React.ChangeEvent<{}>, value: T | null): void;
  defaultValue?: T;
  onClearSearch?(): void;
}

function Autocomplete<T>({
  autofocus = false,
  label,
  filterOptions,
  TextFieldProps,
  renderOption,
  getOptionLabel,
  classes,
  onClearSearch,
  value,
  ...rest
}: AutocompleteProps<T>) {
  const NoPortalPopper = (props: PopperProps) => {
    const { children } = props;
    return (
      <Popper
        {...props}
        placement="bottom"
        disablePortal
        css={popper}
        modifiers={{
          flip: { enabled: true },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'scrollParent',
            escapeWithReference: true,
          },
        }}
      >
        {children}
      </Popper>
    );
  };

  return (
    <MuiAutocomplete<T>
      filterOptions={filterOptions ? filterOptions : (x) => x}
      autoComplete
      includeInputInList
      renderInput={({ InputLabelProps, InputProps, ...params }) => (
        <TextField
          css={textField}
          label={label}
          {...params}
          {...InputProps}
          {...TextFieldProps}
          ref={InputProps.ref}
          inputProps={{
            ...params.inputProps,
            'data-testid': 'your-street-address-input',
          }}
          endAdornment={
            value && (
              <IconButton
                aria-label="Clear input"
                size="small"
                variant="ghost"
                onClick={onClearSearch}
                type="button"
              >
                <IconMigrationUtil legacyIcon={<IconClose />} newIcon={<IconCrossLarge />} />
              </IconButton>
            )
          }
          variant="inverse"
        />
      )}
      renderOption={(option: T, { selected }: AutocompleteRenderOptionState) => {
        return (
          <ListItem
            variant="basic"
            size="medium"
            weight="medium"
            selected={selected}
            rootComponent="div"
          >
            {getOptionLabel?.(option) ?? option}
          </ListItem>
        );
      }}
      popupIcon={null}
      PopperComponent={NoPortalPopper}
      getOptionLabel={getOptionLabel}
      selectOnFocus={false}
      value={value}
      {...rest}
    />
  );
}

export default Autocomplete;
